import {
    Component,
    effect,
    Inject,
    OnInit,
    Signal,
    WritableSignal,
} from '@angular/core';
import {
    FormControl,
    FormGroup,
    Validators,
    ReactiveFormsModule,
} from '@angular/forms';
import {
    MatDialogRef,
    MAT_DIALOG_DATA,
    MatDialogContent,
} from '@angular/material/dialog';
import { ACTIVE } from 'src/app/constants/statusConstant';
import { Project } from 'src/app/models/project-model';
import { ProjectsService } from 'src/app/services/projects.service';
import { PastDateValidator } from 'src/app/validators/past-date.validator';
import { DialogFooterComponent } from '../../dialog-footer/dialog-footer.component';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgFor, NgIf } from '@angular/common';
import { DialogHeaderComponent } from '../../dialog-header/dialog-header.component';
import { VisibleDirective } from '../../../directives/visible.directive';
import { UniversalSnackbarService } from 'src/app/services/universal-snackbar.service';
import { SnackbarMessages as MSG } from 'src/app/enums/snackBar.enum';
import { User } from 'src/app/models/User';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';

interface IProjectFormControls {
    name: FormControl<string>;
    externalContact: FormControl<string>;
    internalContact: FormControl<string>;
    projectLead: FormControl<string>;
    startDate: FormControl<string>;
    endDate: FormControl<string>;
    notes: FormControl<string>;
    clientId: FormControl<string>;
    estimatedHours: FormControl<number | null>;
    nonBillable: FormControl<boolean>;
}

@Component({
    templateUrl: './add-project.component.html',
    styleUrls: ['./add-project.component.scss'],
    imports: [
        DialogHeaderComponent,
        MatDialogContent,
        NgIf,
        NgFor,
        MatProgressBarModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatCheckboxModule,
        MatButtonModule,
        DialogFooterComponent,
        VisibleDirective,
        MatOptionModule,
        MatSelectModule,
        MatIconModule,
    ],
})
export class AddProjectComponent implements OnInit {
    projectForm: FormGroup<IProjectFormControls>;
    loading = false;
    failed = false;
    constructor(
        @Inject(MAT_DIALOG_DATA)
        private data: {
            clientId: string;
            activeUsers: WritableSignal<User[]>;
            projectLeads: Signal<User[]>;
        },
        private dialogRef: MatDialogRef<AddProjectComponent>,
        private projectService: ProjectsService,
        private snackBar: UniversalSnackbarService,
    ) {
        this.watchLoadingState();
    }

    ngOnInit(): void {
        this.initializeForm();
    }

    initializeForm(): void {
        this.projectForm = new FormGroup(
            {
                name: new FormControl(null, [
                    Validators.required.bind(this),
                    Validators.maxLength(100),
                ]),
                externalContact: new FormControl(null, [
                    Validators.required.bind(this),
                    Validators.maxLength(100),
                ]),
                internalContact: new FormControl(
                    {
                        value: null,
                        disabled: this.activeUsers.length < 1 ? true : false,
                    },
                    [Validators.required.bind(this), Validators.maxLength(100)],
                ),
                projectLead: new FormControl(
                    {
                        value: null,
                        disabled: this.projectLeads.length < 1 ? true : false,
                    },
                    [Validators.required.bind(this), Validators.maxLength(100)],
                ),
                startDate: new FormControl(null, [
                    Validators.required.bind(this),
                ]),
                endDate: new FormControl(null),
                notes: new FormControl(null),
                clientId: new FormControl(this.data.clientId, [
                    Validators.required.bind(this),
                ]),
                estimatedHours: new FormControl(null),
                nonBillable: new FormControl(false),
            },
            [PastDateValidator],
        );
    }

    onFormSave(): void {
        this.projectForm.updateValueAndValidity();
        this.projectForm.markAllAsTouched();

        if (!this.projectForm.valid) {
            return;
        }

        this.loading = true;
        const project = new Project({
            name: this.projectForm.controls.name.value,
            externalContact: this.projectForm.controls.externalContact.value,
            internalContact: this.projectForm.controls.internalContact.value,
            projectLead: this.projectForm.controls.projectLead.value,
            startDate: this.projectForm.controls.startDate.value,
            endDate: this.projectForm.controls.endDate.value,
            notes: this.projectForm.controls.notes.value,
            clientId: this.projectForm.controls.clientId.value,
            estimatedHours: this.projectForm.controls.estimatedHours.value,
            nonBillable: this.projectForm.controls.nonBillable.value,
            status: ACTIVE,
        });

        this.projectService.post([], project).subscribe({
            next: (response) => {
                this.loading = false;
                this.dialogRef.close({ success: true, projectId: response });
                this.snackBar.open(MSG.PROJECT_CREATED);
            },
            error: () => {
                this.loading = false;
                this.dialogRef.close({ success: false });
                this.snackBar.open(MSG.PROJECT_CREATED_F);
            },
        });
    }

    onCloseClick(): void {
        this.dialogRef.close(false);
    }

    get clientId() {
        return this.data.clientId;
    }

    get activeUsers() {
        return this.data.activeUsers();
    }

    get projectLeads() {
        return this.data.projectLeads();
    }

    private watchLoadingState() {
        effect(() => {
            if (this.activeUsers.length < 1) {
                this.projectForm.get('internalContact')?.disable();
            } else {
                this.projectForm.get('internalContact')?.enable();
            }
            if (this.projectLeads.length < 1) {
                this.projectForm.get('projectLead')?.disable();
            } else {
                this.projectForm.get('projectLead')?.enable();
            }
        });
    }
}
